import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress
} from '@mui/material';
import {
  Sync as SyncIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Science as TestIcon,
  History as HistoryIcon,
  AccountBalance as QuickBooksIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface QuickBooksIntegration {
  _id: string;
  location: {
    _id: string;
    name: string;
  };
  companyId: string;
  syncStatus: 'active' | 'error' | 'expired' | 'disabled';
  lastSyncAt: string;
  syncSettings: {
    autoSync: boolean;
    syncInterval: number;
    syncReservations: boolean;
    syncTransactions: boolean;
    syncCustomers: boolean;
    syncItems: boolean;
  };
  syncStats: {
    totalCustomersSynced: number;
    totalInvoicesSynced: number;
    totalPaymentsSynced: number;
    lastSyncDuration: number;
    errorsCount: number;
  };
  lastError?: {
    message: string;
    timestamp: string;
  };
}

interface SyncRecord {
  _id: string;
  syncType: string;
  status: string;
  localModel: string;
  quickbooksId?: string;
  createdAt: string;
  completedAt?: string;
  error?: {
    message: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`quickbooks-tabpanel-${index}`}
      aria-labelledby={`quickbooks-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const QuickBooksManagement: React.FC = () => {
  const [integrations, setIntegrations] = useState<QuickBooksIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [settingsDialog, setSettingsDialog] = useState<QuickBooksIntegration | null>(null);
  const [syncHistoryDialog, setSyncHistoryDialog] = useState<QuickBooksIntegration | null>(null);
  const [syncHistory, setSyncHistory] = useState<SyncRecord[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/quickbooks/integrations', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to load integrations');

      const data = await response.json();
      setIntegrations(data);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const startOAuthFlow = async (locationId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/quickbooks/auth/start', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ locationId })
      });

      if (!response.ok) throw new Error('Failed to start OAuth flow');

      const data = await response.json();
      window.open(data.authUrl, '_blank', 'width=800,height=600');
    } catch (error: any) {
      setError(error.message);
    }
  };

  const testConnection = async (integrationId: string) => {
    try {
      setSyncing(integrationId);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quickbooks/integrations/${integrationId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Connection test failed');

      const result = await response.json();
      
      if (result.connected) {
        setSuccess(`Connected to ${result.companyName}`);
      } else {
        setError(`Connection failed: ${result.error}`);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSyncing(null);
    }
  };

  const runSync = async (integrationId: string, syncType: string = 'full') => {
    try {
      setSyncing(integrationId);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quickbooks/integrations/${integrationId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ syncType })
      });

      if (!response.ok) throw new Error('Sync failed');

      const result = await response.json();
      setSuccess('Sync completed successfully');
      loadIntegrations(); // Refresh data
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSyncing(null);
    }
  };

  const updateSettings = async (integrationId: string, settings: any) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quickbooks/integrations/${integrationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) throw new Error('Failed to update settings');

      setSuccess('Settings updated successfully');
      setSettingsDialog(null);
      loadIntegrations();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const loadSyncHistory = async (integrationId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quickbooks/integrations/${integrationId}/sync-history?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to load sync history');

      const data = await response.json();
      setSyncHistory(data.syncs);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleShowSyncHistory = (integration: QuickBooksIntegration) => {
    setSyncHistoryDialog(integration);
    loadSyncHistory(integration._id);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'error': return 'error';
      case 'expired': return 'warning';
      case 'disabled': return 'default';
      default: return 'default';
    }
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QuickBooksIcon color="primary" />
        QuickBooks Integration
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Integrations" />
          <Tab label="Setup Guide" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {integrations.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <QuickBooksIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No QuickBooks Integrations Found
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    Connect your RV park locations to QuickBooks to automatically sync customers, invoices, and payments.
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => startOAuthFlow('location_id')}
                    disabled={true}
                  >
                    Connect QuickBooks
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Select a location to connect QuickBooks
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            integrations.map((integration) => (
              <Grid item xs={12} md={6} lg={4} key={integration._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {integration.location.name}
                        </Typography>
                        <Chip 
                          label={integration.syncStatus} 
                          color={getStatusColor(integration.syncStatus) as any}
                          size="small"
                        />
                      </Box>
                      <QuickBooksIcon color="primary" />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Company ID: {integration.companyId}
                    </Typography>

                    {integration.lastSyncAt && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Sync: {formatDate(integration.lastSyncAt)}
                      </Typography>
                    )}

                    {integration.lastError && (
                      <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
                        {integration.lastError.message}
                      </Alert>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Sync Statistics
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            Customers: {integration.syncStats.totalCustomersSynced}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            Invoices: {integration.syncStats.totalInvoicesSynced}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            Payments: {integration.syncStats.totalPaymentsSynced}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            Errors: {integration.syncStats.errorsCount}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={syncing === integration._id ? <CircularProgress size={16} /> : <TestIcon />}
                        onClick={() => testConnection(integration._id)}
                        disabled={syncing === integration._id}
                      >
                        Test
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={syncing === integration._id ? <CircularProgress size={16} /> : <SyncIcon />}
                        onClick={() => runSync(integration._id)}
                        disabled={syncing === integration._id}
                      >
                        Sync
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => setSettingsDialog(integration)}
                      >
                        <SettingsIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleShowSyncHistory(integration)}
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              QuickBooks Integration Setup Guide
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              To connect QuickBooks, you'll need to set up environment variables and complete OAuth authorization.
            </Alert>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              1. Environment Variables Required
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
              QUICKBOOKS_CLIENT_ID=your_app_client_id<br />
              QUICKBOOKS_CLIENT_SECRET=your_app_client_secret<br />
              QUICKBOOKS_REDIRECT_URI=https://your-domain.com/api/quickbooks/callback
            </Box>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              2. QuickBooks App Configuration
            </Typography>
            <Typography variant="body2" paragraph>
              • Create an app at developer.intuit.com<br />
              • Add your redirect URI to the app settings<br />
              • Copy the Client ID and Client Secret to your environment variables
            </Typography>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              3. Supported Features
            </Typography>
            <Typography variant="body2" paragraph>
              • Customer sync from guest profiles<br />
              • Invoice creation from reservations<br />
              • Payment tracking from transactions<br />
              • Automated sync scheduling<br />
              • Error handling and retry logic
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Settings Dialog */}
      <Dialog 
        open={!!settingsDialog} 
        onClose={() => setSettingsDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>QuickBooks Sync Settings</DialogTitle>
        <DialogContent>
          {settingsDialog && (
            <Box sx={{ pt: 1 }}>
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">Sync Options</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settingsDialog.syncSettings.autoSync}
                        onChange={(e) => setSettingsDialog({
                          ...settingsDialog,
                          syncSettings: {
                            ...settingsDialog.syncSettings,
                            autoSync: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Enable Automatic Sync"
                  />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settingsDialog.syncSettings.syncCustomers}
                        onChange={(e) => setSettingsDialog({
                          ...settingsDialog,
                          syncSettings: {
                            ...settingsDialog.syncSettings,
                            syncCustomers: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Sync Customers"
                  />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settingsDialog.syncSettings.syncReservations}
                        onChange={(e) => setSettingsDialog({
                          ...settingsDialog,
                          syncSettings: {
                            ...settingsDialog.syncSettings,
                            syncReservations: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Sync Reservations as Invoices"
                  />
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={settingsDialog.syncSettings.syncTransactions}
                        onChange={(e) => setSettingsDialog({
                          ...settingsDialog,
                          syncSettings: {
                            ...settingsDialog.syncSettings,
                            syncTransactions: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Sync Payments"
                  />
                </FormGroup>
              </FormControl>

              <TextField
                fullWidth
                label="Sync Interval (minutes)"
                type="number"
                value={settingsDialog.syncSettings.syncInterval}
                onChange={(e) => setSettingsDialog({
                  ...settingsDialog,
                  syncSettings: {
                    ...settingsDialog.syncSettings,
                    syncInterval: parseInt(e.target.value) || 60
                  }
                })}
                InputProps={{ inputProps: { min: 15, max: 1440 } }}
                helperText="How often to run automatic sync (15-1440 minutes)"
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => settingsDialog && updateSettings(settingsDialog._id, {
              syncSettings: settingsDialog.syncSettings
            })}
            variant="contained"
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync History Dialog */}
      <Dialog 
        open={!!syncHistoryDialog} 
        onClose={() => setSyncHistoryDialog(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Sync History</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>QuickBooks ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncHistory.map((sync) => (
                  <TableRow key={sync._id}>
                    <TableCell>
                      <Chip label={sync.syncType} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={sync.status} 
                        color={getSyncStatusColor(sync.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{sync.localModel}</TableCell>
                    <TableCell>{sync.quickbooksId || '-'}</TableCell>
                    <TableCell>{formatDate(sync.createdAt)}</TableCell>
                    <TableCell>
                      {sync.error && (
                        <Typography variant="caption" color="error">
                          {sync.error.message}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncHistoryDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuickBooksManagement;